import * as domUtil from '../../util/dom';
import * as device from '../../util/device';
import * as videoAttributes from '../../util/video-attributes';
import { renderCues } from '../vtt';

const CAPTIONS_STORAGE_KEY = "captionsOn";
const NAMESPACE = 'hlssubtitles';
const fullscreenEvents = 'fullscreenchange, mozfullscreenchange, MSFullscreenChange, webkitfullscreenchange';

const isVisible = () => domUtil.getStoredData(CAPTIONS_STORAGE_KEY) === 'true';
const getCurrentInstanceNamespace = (videoElement) => {
    const uid = videoAttributes.getUID(videoElement) || videoAttributes.generateAndSetUID(videoElement);

    return `${NAMESPACE}-${uid}`;
};

/**
 * Abstract class for HLS subtitles. HLS.js and native HLS have their own implementations of hide() and show()
 */
export default class HlsSubtitles {
    constructor(videoElement, api) {
        this.videoElement = videoElement;
        this.api = api;

        this.activeTextTrack = null;

        this.domElement = null;

        this.instanceNamespace = getCurrentInstanceNamespace(videoElement);

        this._setupInitialEventListeners();
    }

    _onSizeChanged() {
        if (!this.domElement) {
            return;
        }

        const fontSize = Math.max(this.videoElement.offsetWidth * 0.0375, 15);
        this.domElement.style.fontSize = `${fontSize}px`;
    }

    _setupInitialEventListeners() {
        domUtil.addEvents(this.videoElement.textTracks, 'addtrack', this._onAddTrack.bind(this), NAMESPACE);

        this.api.on('subtitles.controls', this._onControls.bind(this));

        domUtil.addEvents(this.videoElement.textTracks, 'change', this._updateSubtitleElementVisibility.bind(this), NAMESPACE);

        domUtil.addEvents(window, 'resize', this._onSizeChanged.bind(this), this.instanceNamespace);
        domUtil.addEvents(document, fullscreenEvents, this._onSizeChanged.bind(this), this.instanceNamespace);
    }

    _createSubtitlesElement() {
        this.domElement = document.createElement('div');
        this.domElement.className = 'vtt-captions';

        if (this.activeTextTrack) {
            this.domElement.style.display = this.activeTextTrack.mode === 'hidden' ?
                'block' : 'none';
        }

        this._onSizeChanged();

        this.videoElement.parentNode.appendChild(this.domElement);
    }

    _findTextTrack() {
        for (let i = 0; i < this.videoElement.textTracks.length; i++) {
            const textTrack = this.videoElement.textTracks[i];
            if (textTrack.language === 'sv') {
                return textTrack;
            }
        }

        return null;
    }

    _onAddTrack() {
        const textTrack = this._findTextTrack();
        if (!textTrack) {
            return;
        }

        this.api.fire('captionsloaded', { fromManifest: true });

        this.activeTextTrack = textTrack;

        if (isVisible()) {
            this.show();
        } else {
            this.hide();
        }

        // In iOS, the browser will render the subtitles itself
        if (device.isIOS()) {
            return;
        }

        if (!this.domElement) {
            this._createSubtitlesElement();
        }

        domUtil.addEvents(this.activeTextTrack, 'cuechange', this._onCueChange.bind(this), NAMESPACE);
    }

    _onControls(controlsEvent) {
        if (this.domElement) {
            domUtil.toggleClass(this.domElement, 'elevated', controlsEvent.detail.visible);
        }
    }

    _onCueChange(cueEvent) {
        const activeCues = cueEvent.target.activeCues;
        renderCues(this.domElement, cueEvent.target.activeCues);
    }

    _updateSubtitleElementVisibility() {
        if (!this.activeTextTrack) {
            return;
        }

        // Keep local storage in sync with native player
        if (device.isIOS()) {
            const visible = this.activeTextTrack.mode === 'showing';

            if (visible) {
                domUtil.setStoredData(CAPTIONS_STORAGE_KEY, true);
            } else {
                domUtil.setStoredData(CAPTIONS_STORAGE_KEY, false);
            }

            return;
        }

        // In Safari, the text track mode may be changed by the browser to 'showing'.
        // This make sure the text track mode is synced with local storage.
        if (isVisible()) {
            this.show();
        } else {
            this.hide();
        }

        // The textTrack mode can either be disabled, hidden, or showing. We should
        // enable the custom subtitles element when textTrack is set to hidden.

        if (this.domElement) {
            this.domElement.style.display = this.activeTextTrack.mode === 'hidden' ?
                'block' : 'none';
        }
    }

    initialize() {
        if (this.videoElement.textTracks.length) {
            this._onAddTrack();
        }
    }

    show() {
        throw new Error('HlsSubtitles is abstract and `show` must be implemented');
    }

    hide() {
        throw new Error('HlsSubtitles is abstract and `hide` must be implemented');
    }

    isEnabled() {
        return Boolean(this.activeTextTrack);
    }

    detach() {
        if (this.domElement && this.domElement.parentNode) {
            this.domElement.parentNode.removeChild(this.domElement);
        }

        this.domElement = null;

        if (this.activeTextTrack) {
            domUtil.removeEvents(this.activeTextTrack, 'cuechange', NAMESPACE);
        }

        if (this.videoElement.textTracks) {
            domUtil.removeEvents(this.videoElement.textTracks, 'addtrack, change', NAMESPACE);
        }

        domUtil.removeEvents(window, 'resize', this.instanceNamespace);
        domUtil.removeEvents(document, fullscreenEvents, this.instanceNamespace);
    }
}



// WEBPACK FOOTER //
// ./src/js/modules/hls/hls-subtitles.js